// =============================================================================
// MÓDULO API BASE DE DATOS - SISTEMA RSVP
// =============================================================================

export class DatabaseAPI {
    constructor(config = {}) {
        this.config = {
            apiUrl: config.apiUrl || '/api/rsvp.php',
            timeout: config.timeout || 15000,
            retries: config.retries || 2,
            ...config
        };

        console.log('🔧 Database API inicializada:', this.config);
    }

    /**
     * Guardar confirmación RSVP
     */
    async saveRSVP(formData, evento) {
        try {
            console.log('📝 Guardando RSVP...');
            console.log('📊 Evento:', evento);
            console.log('📋 Datos:', formData);

            // Preparar datos
            const data = {
                ...formData,
                evento: evento,
                timestamp: new Date().toISOString()
            };

            // Limpiar honeypot
            if (data.honeypot && data.honeypot.trim() !== '') {
                throw new Error('Spam detectado');
            }
            delete data.honeypot;

            const response = await this.makeRequest('POST', '', data);
            
            if (response.success) {
                console.log('✅ RSVP guardado exitosamente');
                return response;
            } else {
                throw new Error(response.error || 'Error desconocido al guardar');
            }

        } catch (error) {
            console.error('❌ Error guardando RSVP:', error);
            throw error;
        }
    }

    /**
     * Verificar si un email ya existe para un evento
     */
    async checkEmailExists(email, evento) {
        try {
            console.log('🔍 Verificando email:', email, 'para evento:', evento);
            
            const response = await this.makeRequest('GET', `?action=checkEmail&evento=${encodeURIComponent(evento)}&email=${encodeURIComponent(email)}`);
            
            if (response.success) {
                console.log('✅ Verificación de email completada:', response);
                return response;
            } else {
                throw new Error(response.error || 'Error verificando email');
            }

        } catch (error) {
            console.error('❌ Error verificando email:', error);
            throw error;
        }
    }

    /**
     * Leer confirmaciones de un evento
     */
    async getConfirmaciones(evento) {
        try {
            console.log('📖 Leyendo confirmaciones para:', evento);
            
            const response = await this.makeRequest('GET', `?action=confirmaciones&evento=${encodeURIComponent(evento)}`);
            
            if (response.success) {
                console.log('✅ Confirmaciones leídas:', response.data.length, 'registros');
                return response.data;
            } else {
                throw new Error(response.error || 'Error leyendo confirmaciones');
            }

        } catch (error) {
            console.error('❌ Error leyendo confirmaciones:', error);
            throw error;
        }
    }

    /**
     * Obtener estadísticas de un evento
     */
    async getEstadisticas(evento) {
        try {
            console.log('📊 Obteniendo estadísticas para:', evento);
            
            const response = await this.makeRequest('GET', `?action=estadisticas&evento=${encodeURIComponent(evento)}`);
            
            if (response.success) {
                console.log('✅ Estadísticas obtenidas:', response.data);
                return response.data;
            } else {
                throw new Error(response.error || 'Error obteniendo estadísticas');
            }

        } catch (error) {
            console.error('❌ Error obteniendo estadísticas:', error);
            throw error;
        }
    }

    /**
     * Test de conexión
     */
    async testConnection() {
        try {
            console.log('🧪 Probando conexión con API...');
            
            const response = await this.makeRequest('GET', '?action=test');
            
            if (response.success) {
                console.log('✅ Test de conexión exitoso:', response);
                return response;
            } else {
                throw new Error(response.error || 'Error en test de conexión');
            }

        } catch (error) {
            console.error('❌ Error en test de conexión:', error);
            throw error;
        }
    }

    /**
     * Exportar confirmaciones como CSV
     */
    async exportCSV(evento) {
        try {
            console.log('📤 Exportando CSV para:', evento);
            
            const url = `${this.config.apiUrl}?action=export&evento=${encodeURIComponent(evento)}`;
            
            // Crear enlace de descarga
            const link = document.createElement('a');
            link.href = url;
            link.download = `${evento}_confirmaciones_${new Date().toISOString().split('T')[0]}.csv`;
            link.click();

            console.log('✅ CSV exportado');
            return { success: true, message: 'CSV descargado' };

        } catch (error) {
            console.error('❌ Error exportando CSV:', error);
            throw error;
        }
    }

    /**
     * Realizar request HTTP con reintentos
     */
    async makeRequest(method, endpoint, data = null) {
        const url = this.config.apiUrl + endpoint;
        
        for (let attempt = 1; attempt <= this.config.retries; attempt++) {
            try {
                console.log(`🌐 Request ${method} (intento ${attempt}):`, url);
                
                const options = {
                    method: method,
                    headers: {
                        'Content-Type': 'application/json',
                    },
                    signal: AbortSignal.timeout(this.config.timeout)
                };

                if (data && method !== 'GET') {
                    options.body = JSON.stringify(data);
                }

                const response = await fetch(url, options);
                
                if (!response.ok) {
                    throw new Error(`HTTP ${response.status}: ${response.statusText}`);
                }

                const result = await response.json();
                console.log('📨 Respuesta recibida:', result);
                
                return result;

            } catch (error) {
                console.warn(`⚠️ Intento ${attempt} falló:`, error.message);
                
                if (attempt === this.config.retries) {
                    throw new Error(`Error después de ${this.config.retries} intentos: ${error.message}`);
                }
                
                // Esperar antes del siguiente intento
                await new Promise(resolve => setTimeout(resolve, 1000 * attempt));
            }
        }
    }

    /**
     * Validar configuración
     */
    isConfigured() {
        const configured = !!this.config.apiUrl && this.config.apiUrl !== '';
        console.log('🔍 API configurada:', configured);
        return configured;
    }
}

// Integración con formularios
export function enhanceFormsWithDatabase(databaseAPI) {
    console.log('🔗 Mejorando formularios con Database API...');
    
    const forms = document.querySelectorAll('.gform');
    console.log('📝 Formularios encontrados:', forms.length);
    
    forms.forEach((form, index) => {
        console.log(`📋 Configurando formulario ${index + 1}`);
        
        form.addEventListener('submit', async (e) => {
            e.preventDefault();
            
            const submitBtn = form.querySelector('button[type="submit"]');
            const originalText = submitBtn.dataset.originalText || submitBtn.textContent;
            
            try {
                console.log('📝 Procesando envío de formulario...');
                
                // Validar configuración
                if (!databaseAPI.isConfigured()) {
                    throw new Error('API de base de datos no está configurada correctamente');
                }

                // Mostrar loading
                submitBtn.disabled = true;
                submitBtn.innerHTML = '<span class="spinner-border spinner-border-sm me-2"></span>Enviando...';

                // Preparar datos
                const formData = new FormData(form);
                const data = Object.fromEntries(formData.entries());
                
                // Procesar checkboxes correctamente
                const checkboxes = form.querySelectorAll('input[type="checkbox"]');
                checkboxes.forEach(checkbox => {
                    if (!checkbox.checked) {
                        // Si el checkbox no está marcado, usar el valor del campo oculto correspondiente
                        const hiddenField = form.querySelector(`input[name="${checkbox.name}_hidden"]`);
                        if (hiddenField) {
                            data[checkbox.name] = hiddenField.value;
                        } else {
                            data[checkbox.name] = 'No';
                        }
                    }
                });
                
                console.log('📋 Datos del formulario procesados:', data);

                // Validaciones básicas
                if (!data.nombre || data.nombre.trim() === '') {
                    throw new Error('El nombre es requerido');
                }
                
                // Validar email (ahora es obligatorio)
                if (!data.email || data.email.trim() === '') {
                    throw new Error('El email es requerido');
                }
                
                const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
                if (!emailRegex.test(data.email)) {
                    throw new Error('El email no tiene un formato válido');
                }

                // Obtener nombre del evento
                const evento = data.evento || data.formGoogleSheetName || form.dataset.evento || 'default';
                
                // Verificar si el email ya existe
                const emailCheck = await databaseAPI.checkEmailExists(data.email, evento);
                
                if (emailCheck.exists) {
                    // Email duplicado - mostrar modal de confirmación
                    const userChoice = await showEmailDuplicateModal(emailCheck.data, data.email);
                    
                    if (userChoice === 'cancel') {
                        throw new Error('Envío cancelado por el usuario');
                    } else if (userChoice === 'change') {
                        // Enfocar el campo email para que el usuario lo cambie
                        const emailField = form.querySelector('#email');
                        if (emailField) {
                            emailField.focus();
                            emailField.select();
                        }
                        throw new Error('Por favor, cambia el email y vuelve a enviar');
                    }
                    // Si userChoice === 'update', continuar con el guardado
                }
                
                console.log('📊 Guardando en evento:', evento);

                // Guardar en base de datos
                const result = await databaseAPI.saveRSVP(data, evento);
                console.log('✅ Formulario enviado exitosamente:', result);

                // Mostrar mensaje de éxito
                showSuccessMessage(form);

            } catch (error) {
                console.error('❌ Error enviando formulario:', error);
                showErrorMessage(form, error.message);
                
                // Si el error es por cambio de email, asegurar que el formulario esté visible
                if (error.message.includes('cambia el email')) {
                    resetFormToVisible(form);
                }
            } finally {
                // Restaurar botón después de un delay
                setTimeout(() => {
                    submitBtn.disabled = false;
                    submitBtn.textContent = originalText;
                }, 2000);
            }
        });
    });
}

function showSuccessMessage(form) {
    console.log('✅ Mostrando mensaje de éxito');
    
    const formElements = form.querySelector('.rsvp__form-elements');
    const thankYouMessage = form.querySelector('.rsvp__thanks');

    if (formElements && thankYouMessage) {
        formElements.classList.add('d-none');
        thankYouMessage.classList.remove('d-none');
    } else {
        // Fallback: mostrar alert
        const alert = document.createElement('div');
        alert.className = 'alert alert-success alert-dismissible fade show mt-3';
        alert.innerHTML = `
            <strong>¡Perfecto!</strong> Tu confirmación ha sido enviada correctamente. ¡Nos vemos en el evento!
            <button type="button" class="btn-close" data-bs-dismiss="alert"></button>
        `;
        form.appendChild(alert);
        
        // Scroll al mensaje
        alert.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
}

function showErrorMessage(form, message) {
    console.log('❌ Mostrando mensaje de error:', message);
    
    // Remover alertas anteriores
    const existingAlerts = form.querySelectorAll('.alert');
    existingAlerts.forEach(alert => alert.remove());
    
    const alert = document.createElement('div');
    alert.className = 'alert alert-danger alert-dismissible fade show mt-3';
    alert.innerHTML = `
        <strong>Error:</strong> ${message}
        <button type="button" class="btn-close" data-bs-dismiss="alert"></button>
    `;
    form.insertBefore(alert, form.firstChild);

    // Scroll al mensaje de error
    alert.scrollIntoView({ behavior: 'smooth', block: 'center' });

    // Auto-remove después de 8 segundos
    setTimeout(() => {
        if (alert.parentNode) {
            alert.remove();
        }
    }, 8000);
}

// Función para mostrar modal de email duplicado
function showEmailDuplicateModal(existingData, email) {
    return new Promise((resolve) => {
        // Crear modal HTML
        const modalHtml = `
            <div class="modal fade" id="emailDuplicateModal" tabindex="-1" aria-hidden="true">
                <div class="modal-dialog modal-dialog-centered">
                    <div class="modal-content">
                        <div class="modal-header bg-warning text-dark">
                            <h5 class="modal-title">
                                <i class="bi bi-exclamation-triangle me-2"></i>
                                Email ya registrado
                            </h5>
                            <button type="button" class="btn-close" data-bs-dismiss="modal"></button>
                        </div>
                        <div class="modal-body">
                            <p class="mb-3">
                                <strong>El email "${email}"</strong> ya tiene una confirmación registrada:
                            </p>
                            <div class="alert alert-info">
                                <strong>Nombre:</strong> ${existingData.nombre}<br>
                                <strong>Confirmación:</strong> ${existingData.asiste}<br>
                                <strong>Fecha:</strong> ${new Date(existingData.fecha_confirmacion).toLocaleDateString('es-AR')}
                            </div>
                            <p>¿Qué te gustaría hacer?</p>
                        </div>
                        <div class="modal-footer">
                            <button type="button" class="btn btn-secondary" data-action="cancel">
                                <i class="bi bi-x-circle me-2"></i>Cancelar
                            </button>
                            <button type="button" class="btn btn-warning" data-action="change">
                                <i class="bi bi-envelope me-2"></i>Cambiar email
                            </button>
                            <button type="button" class="btn btn-success" data-action="update">
                                <i class="bi bi-arrow-clockwise me-2"></i>Actualizar confirmación
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        `;
        
        // Agregar modal al DOM
        document.body.insertAdjacentHTML('beforeend', modalHtml);
        
        const modalElement = document.getElementById('emailDuplicateModal');
        const modal = new bootstrap.Modal(modalElement);
        
        // Manejar clicks en botones
        modalElement.addEventListener('click', (e) => {
            const action = e.target.dataset.action;
            if (action) {
                modal.hide();
                resolve(action);
            }
        });
        
        // Limpiar modal cuando se cierre
        modalElement.addEventListener('hidden.bs.modal', () => {
            modalElement.remove();
        });
        
        // Mostrar modal
        modal.show();
    });
}

// Función para resetear el formulario a visible (cuando hay error de email duplicado)
function resetFormToVisible(form) {
    console.log('🔄 Reseteando formulario a visible');
    
    const formElements = form.querySelector('.rsvp__form-elements');
    const thankYouMessage = form.querySelector('.rsvp__thanks');

    if (formElements && thankYouMessage) {
        // Asegurar que el formulario esté visible y el mensaje de gracias oculto
        formElements.classList.remove('d-none');
        thankYouMessage.classList.add('d-none');
    }
}

// Función helper para inicializar Database API
export function initDatabaseAPI(config) {
    console.log('🚀 Inicializando Database API:', config);
    return new DatabaseAPI(config);
}